import {
   Flex,
   FlexProps,
   Image,
   Stack,
   StackProps,
   Box,
   ImageProps,
   BoxProps,
   Text,
   TextProps,
   HStack,
} from '@chakra-ui/react';
import { faCorner } from '@fortawesome/pro-regular-svg-icons';
import { ReactNode, createContext, useContext, useRef } from 'react';
import { Icon } from '.';

/**
 * A series of components for creating cards
 * @usage -
   <Card>
         <CardImage src={guide.image?.thumbnail} alt={guide.title} />
         <CardContent>
            <Text variant="subtitle">Prep Cab (3 Minutes)</Text>
            <Text variant="muted">
               Version: 3.0
            </Text>
         </CardContent>
         <CardRight>
            <CardActions>
               <IconButton
                  aria-label="Remove Guide"
                  icon={<Icon type={faTrashCan} />}
                  onClick={() => onRemove()}
               />
            </CardActions>
         </CardRight>
      </Card>
 */

type CardListProps = Omit<StackProps, 'spacing' | 'my' | 'role'>;

export function CardList({ children, ...props }: CardListProps) {
   return (
      <Stack spacing={5} my={5} role="list" {...props}>
         {children}
      </Stack>
   );
}

export type CardProps = FlexProps & {
   children?: ReactNode;
   sizing?: CardVariant;
};

export type CardContext = {
   isHovered: boolean;
};

type CardCtx = {
   id: number;
};

let cardIdSerial = 1;

const CardCtx = createContext<CardCtx>({ id: 0 });

type CardVariant = 'md' | 'sm' | 'default';

const cardStyles: Record<CardVariant, FlexProps> = {
   md: {
      minHeight: '112.5px',
      paddingX: 4,
   },
   sm: {
      paddingX: 2,
      paddingY: 3,
      align: 'center',
   },
   default: {},
};

export function Card({ children, sizing = 'default', ...flexProps }: CardProps) {
   const { current: id } = useRef(cardIdSerial++);
   return (
      <Flex
         position="relative"
         bgColor="white"
         borderRadius="lg"
         shadow="md"
         width="100%"
         role="listitem"
         cursor={flexProps.onClick && 'pointer'}
         aria-labelledby={`card-header-${id}`}
         {...cardStyles[sizing]}
         {...flexProps}
      >
         <CardCtx.Provider value={{ id }}>{children}</CardCtx.Provider>
      </Flex>
   );
}

type CardImageProps = ImageProps;

export function CardImage({ alt, ...imageProps }: CardImageProps) {
   return <Image width={150} objectFit="contain" alt={alt} {...imageProps} />;
}

type CardContentProps = FlexProps & {
   children?: ReactNode;
};

export function CardContent({ children, ...flexProps }: CardContentProps) {
   return (
      <Flex direction="column" justify="center" {...flexProps}>
         {children}
      </Flex>
   );
}

type CardRightProps = {
   children?: ReactNode;
};

export function CardRight({ children }: CardRightProps) {
   return (
      <Stack spacing={2} flex={1} direction="row" align="center" justify="flex-end">
         {children}
      </Stack>
   );
}

type CardActionsProps = StackProps;

export function CardActions({ children, spacing = 2, ...boxProps }: CardActionsProps) {
   return (
      <HStack onClick={e => e.stopPropagation()} {...boxProps}>
         {children}
      </HStack>
   );
}

type CardOverlayProps = BoxProps;

export function CardOverlay({ children, ...boxProps }: CardOverlayProps) {
   return (
      <Box
         {...boxProps}
         position="absolute"
         top={0}
         left={0}
         width="100%"
         height="100%"
         backgroundColor="rgba(255,255,255,0.5)"
         opacity={0}
         _hover={{
            opacity: 1,
         }}
         transition="opacity 100ms ease"
      >
         {children}
      </Box>
   );
}

type CardHeaderProps = TextProps;

export function CardHeader({ children }: CardHeaderProps) {
   const { id } = useContext(CardCtx);
   return (
      <Text fontWeight="bold" color="blueGray.800" id={`card-header-${id}`}>
         {children}
      </Text>
   );
}

type CardExpansionProps = BoxProps;

export function CardExpansion({ children }: CardExpansionProps) {
   return (
      <Box
         border="1px solid"
         borderColor="gray.200"
         borderBottomRadius={6}
         borderTop="none"
         mt={0}
         mx={2}
         mb={4}
      >
         {children}
      </Box>
   );
}

type NestedCardContainerProps = FlexProps;

export function NestedCardContainer({ children, ...props }: NestedCardContainerProps) {
   return (
      <Flex alignItems="center" {...props}>
         <Icon
            color="gray.300"
            ml="16"
            mr="12"
            size="2x"
            type={faCorner}
            sx={{ transform: 'rotate(180deg)' }}
         />
         {children}
      </Flex>
   );
}
